import * as ko from 'knockout';
import { uniq } from 'lodash';
import { app } from '../../app';
import i18n from '../../i18n';
import { translate } from '../../i18n_text';
import * as dimensionMetasApi from '../../api/dimension_metas';
import * as dimensionsApi from '../../api/dimensions';
import * as namesApi from '../../api/names';
import * as unitCategoriesApi from '../../api/unit_categories';
import * as unitsApi from '../../api/units';
import { ScheduledVisit, TrialInputApplication } from '../../models/trial';
import { Treatment, TreatmentFactor, TreatmentFactorDimension } from '../../models/treatment';
import { ListRequestParams } from '../../api/request';
import { FormSelectSearchConfiguration } from '../form_select_search';
import { FormTabbedEntitiesConfiguration } from '../form_tabbed_entities';
import { Deferred } from '../../utils/deferred';
import { FLOAT_VALIDATION_RULES } from '../../utils';
import {
  describeProductUnitCategoryConflict,
  findProductUnitCategoryConflicts,
  plannedUnitCategoryByProductId,
  ProductUnitCategoryUsage,
  selectableConcentrationUnitCategories,
} from './input_application_unit_categories';
import { pinSuggestionsToTop } from './trial_applications_paging';

const template =
  require('raw-loader!../../../templates/components/trial_wizard/trial_input_applications_edit.html').default;

export interface InputApplicationCellResult {
  applications: TrialInputApplication[];
}

interface TreatmentSuggestions {
  dimensionMetas: dimensionMetasApi.DimensionMetaData[];
  productIdsByDimensionMetaId: Map<string, string[]>;
}

function buildProductData(id: string, name: string, dimensionMetaId: string): dimensionsApi.DimensionData {
  return {
    id,
    name_json: { default: 'en', en: name },
    anonymized_code: '',
    code: '',
    dimension_meta_id: dimensionMetaId,
    crops: [],
  };
}

function buildTreatmentSuggestions(
  treatmentFactors: TreatmentFactor[],
  dimensionMetasById: Map<string, dimensionMetasApi.DimensionMetaData>
): TreatmentSuggestions {
  const dimensions = treatmentFactors.map((factor) => factor.dimension());
  const dimensionMetaIds = uniq(dimensions.map((dimension) => String(dimension.dimension_meta_id())));

  return {
    dimensionMetas: dimensionMetaIds
      .map((dimensionMetaId) => dimensionMetasById.get(dimensionMetaId))
      .filter(
        (dimensionMeta): dimensionMeta is dimensionMetasApi.DimensionMetaData => dimensionMeta !== undefined
      ),
    productIdsByDimensionMetaId: groupProductIdsByDimensionMetaId(dimensions),
  };
}

function groupProductIdsByDimensionMetaId(dimensions: TreatmentFactorDimension[]): Map<string, string[]> {
  const productIdsByDimensionMetaId = new Map<string, string[]>();
  for (const dimension of dimensions) {
    const dimensionMetaId = String(dimension.dimension_meta_id());
    productIdsByDimensionMetaId.set(
      dimensionMetaId,
      (productIdsByDimensionMetaId.get(dimensionMetaId) ?? []).concat(dimension.id())
    );
  }

  return productIdsByDimensionMetaId;
}

class InputApplicationTab {
  // The group gates the product list, so it has to be validated in its own
  // right: without it, an empty form only complained about the product, which
  // cannot be picked until a group is chosen. Only for a new product, though —
  // an existing one resolves its group from a separate lookup that can come
  // back empty, and requiring it would block saving a row that already saved.
  dimensionMeta = ko
    .observable<dimensionMetasApi.DimensionMetaData | null>(null)
    .extend({ required: { onlyIf: () => this.serverId === null } });
  product = ko.observable<dimensionsApi.DimensionData | null>(null).extend({ required: true });
  concentration = ko.observable('').extend({ required: true, ...FLOAT_VALIDATION_RULES });
  unit = ko.observable<unitsApi.UnitData | null>(null).extend({ required: true });
  method = ko.observable<namesApi.NameI18nData | null>(null).extend({ required: true });

  duplicateProductError = ko.observable('');
  inconsistentProductUnitCategoryError = ko.observable('');

  errors = ko.validation.group(
    [this.dimensionMeta, this.product, this.concentration, this.unit, this.method],
    {
      deep: false,
    }
  );

  dimensionMetaSearchConfig: FormSelectSearchConfiguration<dimensionMetasApi.DimensionMetaData> = {
    getSummaryName: (dimensionMeta) => dimensionMeta.name_json,
    list: async (params) =>
      pinSuggestionsToTop(
        await dimensionMetasApi.listExcludingDates({ ...params, usage: 'any' }),
        this.suggestions.dimensionMetas,
        params.name_prefix ?? '',
        (dimensionMeta) => translate(dimensionMeta.name_json)
      ),
    entity: this.dimensionMeta,
  };

  productSearchConfig: FormSelectSearchConfiguration<dimensionsApi.DimensionData> = {
    getSummaryName: (dimension) => dimension.name_json,
    list: async (params) => {
      const dimensionMeta = this.dimensionMeta();
      if (!dimensionMeta) {
        return [];
      }

      const [results, suggestedProducts] = await Promise.all([
        dimensionsApi.list(dimensionMeta.id, {}, params),
        this.listSuggestedProducts(dimensionMeta, params),
      ]);

      return pinSuggestionsToTop(
        results,
        suggestedProducts,
        params.name_prefix ?? '',
        (product) => translate(product.name_json)
      );
    },
    entity: this.product,
  };

  methodSearchConfig: FormSelectSearchConfiguration<namesApi.NameI18nData> = {
    getSummaryName: (method) => method.name_json,
    list: (params) => namesApi.list('input_application_methods', params),
    entity: this.method,
  };

  unitSearchConfig: FormSelectSearchConfiguration<unitsApi.UnitData> = {
    getSummaryName: (unit) => unit.name,
    list: (params) =>
      unitsApi.list({ ...params, categories: this.selectableUnitCategories(this.product()?.id ?? null) }),
    entity: this.unit,
  };

  // an observable so KO passes it as a callable to component `enable` params.
  // Outside drafts the product of a saved application is read-only: changing
  // it would remove the old (visit, treatment, product) plan entry, which
  // devices validate their uploads against.
  canEditProduct: KnockoutObservable<boolean>;

  private subscriptions: KnockoutSubscription[] = [];

  constructor(
    public serverId: number | null,
    canEditProduct: boolean,
    private selectableUnitCategories: (productId: string | null) => string[],
    private suggestions: TreatmentSuggestions,
    private listProductIdsInOtherTabs: (tab: InputApplicationTab) => string[]
  ) {
    this.canEditProduct = ko.observable(canEditProduct);
    this.subscriptions.push(
      this.dimensionMeta.subscribe(() => {
        // the product belongs to the previous group
        this.product(null);
      }),
      this.product.subscribe((product) => {
        const unitCategory = this.unit()?.unit_category;
        if (unitCategory && !this.selectableUnitCategories(product?.id ?? null).includes(unitCategory)) {
          this.unit(null);
        }
      })
    );
  }

  dispose() {
    this.subscriptions.forEach((subscription) => subscription.dispose());
    this.subscriptions = [];
  }

  private listSuggestedProducts(
    dimensionMeta: dimensionMetasApi.DimensionMetaData,
    params: ListRequestParams
  ): Promise<dimensionsApi.DimensionData[]> {
    const productIdsInOtherTabs = this.listProductIdsInOtherTabs(this);
    const treatmentProductIds =
      this.suggestions.productIdsByDimensionMetaId.get(String(dimensionMeta.id)) ?? [];
    const productIds = treatmentProductIds.filter((productId) => !productIdsInOtherTabs.includes(productId));
    if (productIds.length === 0) {
      return Promise.resolve([]);
    }

    return dimensionsApi.list(dimensionMeta.id, { ids: productIds }, params);
  }

  title = ko.pureComputed(() => {
    const product = this.product();
    if (product) {
      return translate(product.name_json);
    }

    return i18n.t('New product')();
  });

  hasErrors() {
    return (
      this.errors().length > 0 ||
      !!this.duplicateProductError() ||
      !!this.inconsistentProductUnitCategoryError()
    );
  }

  showErrors() {
    this.errors.showAllMessages();
  }
}

/** Parameters of the applications popup for one treatment cell. */
export interface InputApplicationsEditParams {
  applications: TrialInputApplication[];
  // the trial's applications outside this cell: a product's unit category
  // must agree with them
  otherApplications: TrialInputApplication[];
  scheduledVisit: ScheduledVisit;
  treatment: Treatment;
  /** Factors of the treatment, in display order. */
  treatmentFactors: TreatmentFactor[];
  isDraft: boolean;
}

class TrialInputApplicationsEdit {
  tabs = ko.observableArray<InputApplicationTab>();
  canRemoveTabs: boolean;
  validationError = ko.observable('');
  inconsistentProductUnitCategoryError = ko.observable('');
  loading = ko.observable(true);
  loadError = ko.observable('');

  tabsConfig: FormTabbedEntitiesConfiguration<InputApplicationTab>;

  private result: Deferred<InputApplicationCellResult>;
  private scheduledVisit: ScheduledVisit;
  private treatment: Treatment;
  private otherApplications: TrialInputApplication[];
  private isDraft: boolean;
  // outside this cell: a product planned there in one category can only take
  // units of that category here
  private plannedUnitCategories: Map<string, string>;
  private unitCategoryNames = new Map<string, string>();
  private suggestions: TreatmentSuggestions = { dimensionMetas: [], productIdsByDimensionMetaId: new Map() };

  constructor(params: InputApplicationsEditParams & { result: Deferred<InputApplicationCellResult> }) {
    this.result = params.result;
    this.scheduledVisit = params.scheduledVisit;
    this.treatment = params.treatment;
    this.otherApplications = params.otherApplications;
    this.plannedUnitCategories = plannedUnitCategoryByProductId(
      params.otherApplications
        // saved before the category travelled with the payload: unknown
        .filter((application) => application.concentrationUnitCategory())
        .map((application) => ({
          productId: application.productId(),
          unitCategory: application.concentrationUnitCategory(),
        }))
    );
    this.isDraft = params.isDraft;
    this.canRemoveTabs = params.isDraft;

    this.tabsConfig = {
      title: i18n.t('Products')(),
      addTitle: i18n.t('Add product')(),
      missingTitle: i18n.t('No products')(),
      entities: this.tabs,

      // no disableSupport: a product in the mix has no hidden/visible concept,
      // so the eye affordance is not rendered at all

      // an unsaved tab is always removable: it only exists in the popup
      canRemove: (tab) => tab.serverId === null || this.canRemoveTabs,
      add: () => {
        const tab = new InputApplicationTab(
          null,
          true,
          this.selectableUnitCategoriesFor(true, ''),
          this.suggestions,
          this.listProductIdsInOtherTabs
        );
        this.tabs.push(tab);
        return tab;
      },
      remove: (tab) => {
        tab.dispose();
        this.tabs.remove(tab);
      },
      hasErrors: (tab) => tab.hasErrors(),
      showErrors: (tab) => tab.showErrors(),
      actions: [],
      getSummaryName: (tab) => tab.title(),
      selectedEntity: ko.observable(null),
    };

    void this.load(params.applications, params.treatmentFactors);
  }

  private async load(applications: TrialInputApplication[], treatmentFactors: TreatmentFactor[]) {
    try {
      await this.loadTabs(applications, treatmentFactors);
    } catch (e) {
      // everything below the loading gate, Apply and Cancel included, is only
      // rendered once loading clears: leaving it set would strand the user in
      // a popup they cannot even close
      this.loadError(i18n.t('Could not load the products and application methods.')());
    } finally {
      this.loading(false);
    }
  }

  private async loadTabs(applications: TrialInputApplication[], treatmentFactors: TreatmentFactor[]) {
    const methods = await namesApi.list('input_application_methods', {});
    const dimensionMetas = await dimensionMetasApi.listExcludingDates({ usage: 'any' });
    const unitCategories = await unitCategoriesApi.list();
    this.unitCategoryNames = new Map(unitCategories.map((category) => [category.slug, category.name]));
    const methodsById = new Map(methods.map((method) => [method.id, method]));
    const dimensionMetasById = new Map(
      dimensionMetas.map((dimensionMeta) => [String(dimensionMeta.id), dimensionMeta])
    );
    this.suggestions = buildTreatmentSuggestions(treatmentFactors, dimensionMetasById);

    for (const application of applications) {
      // a tab that only exists locally (serverId null) stays fully editable
      const isEditable = this.isDraft || application.serverId === null;
      const tab = new InputApplicationTab(
        application.serverId,
        isEditable,
        this.selectableUnitCategoriesFor(isEditable, application.concentrationUnitCategory()),
        this.suggestions,
        this.listProductIdsInOtherTabs
      );
      const dimensionMeta = dimensionMetasById.get(String(application.productDimensionMetaId()));
      tab.dimensionMeta(dimensionMeta ?? null);
      tab.product(
        buildProductData(
          application.productId(),
          application.productName(),
          application.productDimensionMetaId()
        )
      );
      tab.concentration(application.concentration());
      const unitId = application.concentrationUnitId();
      tab.unit(
        unitId === null
          ? null
          : {
              id: unitId.toString(),
              name: application.concentrationUnitName(),
              description: '',
              unit_category: application.concentrationUnitCategory(),
            }
      );
      tab.method(methodsById.get(application.applicationMethodId()?.toString()) ?? null);
      this.tabs.push(tab);
    }
  }

  private listProductIdsInOtherTabs = (tab: InputApplicationTab): string[] =>
    this.tabs()
      .filter((otherTab) => otherTab !== tab)
      .map((otherTab) => otherTab.product()?.id)
      .filter((productId): productId is string => productId !== undefined);

  private selectableUnitCategoriesFor(
    isEditable: boolean,
    initialUnitCategory: string
  ): (productId: string | null) => string[] {
    return (productId) =>
      selectableConcentrationUnitCategories(
        isEditable,
        initialUnitCategory,
        (productId && this.plannedUnitCategories.get(productId)) || ''
      );
  }

  private validate(): boolean {
    this.validationError('');
    this.inconsistentProductUnitCategoryError('');
    let valid = true;
    const seenProductIds = new Set<string>();
    for (const tab of this.tabs()) {
      tab.duplicateProductError('');
      tab.inconsistentProductUnitCategoryError('');
      if (tab.hasErrors()) {
        tab.showErrors();
        valid = false;
      }
      const product = tab.product();
      if (product) {
        if (seenProductIds.has(product.id)) {
          tab.duplicateProductError(i18n.t('The same product cannot be repeated')());
          this.validationError(i18n.t('The same product cannot be repeated')());
          valid = false;
        }
        seenProductIds.add(product.id);
      }
    }

    return this.validateProductUnitCategories() && valid;
  }

  // Every application of a product, in this cell (the tabs) and in the rest of
  // the trial, must use one unit category. The message lists every cell
  // involved, so the user can find them in the applications table.
  private validateProductUnitCategories(): boolean {
    const usages: ProductUnitCategoryUsage[] = [];
    const tabsByProductId = new Map<string, InputApplicationTab>();
    for (const tab of this.tabs()) {
      const product = tab.product();
      const unitCategory = tab.unit()?.unit_category;
      if (!product || !unitCategory) {
        continue;
      }
      tabsByProductId.set(product.id, tab);
      usages.push({
        productId: product.id,
        productName: translate(product.name_json),
        unitCategory,
        scheduledVisitName: translate(this.scheduledVisit.nameJson()),
        treatmentName: translate(this.treatment.nameJson()),
      });
    }
    for (const application of this.otherApplications) {
      const scheduledVisitName = translate(application.scheduledVisit.nameJson());
      // An application saved before the category travelled with the payload
      // cannot be compared.
      if (!application.concentrationUnitCategory()) {
        continue;
      }

      usages.push({
        productId: application.productId(),
        productName: application.productName(),
        unitCategory: application.concentrationUnitCategory(),
        scheduledVisitName,
        treatmentName: translate(application.treatment.nameJson()),
      });
    }

    const conflicts = findProductUnitCategoryConflicts(usages).filter((conflict) =>
      tabsByProductId.has(conflict.productId)
    );
    if (conflicts.length === 0) {
      return true;
    }

    const descriptions = conflicts.map((conflict) => {
      const description = describeProductUnitCategoryConflict(conflict, this.unitCategoryNames);
      tabsByProductId.get(conflict.productId)?.inconsistentProductUnitCategoryError(description);
      return description;
    });
    this.inconsistentProductUnitCategoryError(
      i18n.t('The unit category of a product must be the same in all its applications: {{details}}', {
        details: descriptions.join('; '),
        // the message is bound as text, so the "/" in the cell names must not
        // come out as an HTML entity
        interpolation: { escapeValue: false },
      })()
    );

    return false;
  }

  onSave = () => {
    if (!this.validate()) {
      return;
    }

    const applications = this.tabs().map((tab) => {
      const application = new TrialInputApplication(this.scheduledVisit, this.treatment);
      application.serverId = tab.serverId;

      const product = tab.product();
      application.productId(product ? product.id : '');
      application.productName(product ? translate(product.name_json) : '');

      const dimensionMeta = tab.dimensionMeta();
      application.productDimensionMetaId(dimensionMeta ? dimensionMeta.id : '');
      application.concentration(tab.concentration());

      const unit = tab.unit();
      application.concentrationUnitId(unit ? parseInt(unit.id, 10) : null);
      application.concentrationUnitName(unit ? unit.name : '');
      application.concentrationUnitCategory(unit?.unit_category ?? '');

      const method = tab.method();
      application.applicationMethodId(parseInt(method?.id ?? '', 10) || null);
      // kept in sync with the id so the table cell summary updates without a reload
      application.applicationMethodName(method ? translate(method.name_json) : '');

      return application;
    });

    this.result.resolve({ applications });
  };

  onCancel = () => {
    this.result.reject();
  };

  dispose() {
    this.tabs().forEach((tab) => tab.dispose());
  }
}

const trialInputApplicationsEdit = {
  name: 'trial-input-applications-edit',
  viewModel: TrialInputApplicationsEdit,
  template,
};

ko.components.register(trialInputApplicationsEdit.name, trialInputApplicationsEdit);

export function openInputApplicationsEdit(
  options: InputApplicationsEditParams & { title: string }
): Promise<InputApplicationCellResult> {
  const { title, ...params } = options;

  return app.formsStackController.push({
    isBig: true,
    showNav: true,
    title,
    name: trialInputApplicationsEdit.name,
    params: {
      ...params,
      result: new Deferred<InputApplicationCellResult>(),
    },
  });
}
